import { Observable } from 'rxjs';

import { AuthLevel, Authorize } from '../private/authorize.decorator';
import { constructUrl } from '../private/utils/httpParamsUtils';
import { IApiUrls } from '../private/global.interface';
import {
  AxiosInstanceType,
  SlAxiosInstanceService,
} from '../private/services/sl-axios-instance.service';
import { SlApiContext } from '../private/api-context';
import {
  AttemptResType,
  ContextAttempt,
  ICode,
  ICodeAssistantApi,
  ICodeAssistantRes,
} from '../private/code-assistant-api/code-assistant-api.interface';
import { SlFetchInstanceService } from '../private/services/sl-fetch-instance.service';
import { Service, Container, CourseTrack } from '../../symphony';

@Service()
export class CodeAssistantApi implements ICodeAssistantApi {
  private environmentUrl: string;

  private axiosInstance: AxiosInstanceType;

  private fetchService: SlFetchInstanceService;

  constructor() {
    this.environmentUrl = (
      Container.take('global', 'envUrl') as IApiUrls
    ).codeAssistantHost;
    this.axiosInstance = Container.take(
      SlApiContext,
      SlAxiosInstanceService,
    ).axios;
    this.fetchService = Container.take(
      SlApiContext,
      SlFetchInstanceService,
    );
  }

  @Authorize(AuthLevel.authenticated)
  public explainTheCode(codes: ICode[], track: CourseTrack): Observable<ICodeAssistantRes> {
    const url = constructUrl(this.environmentUrl, 'explainthecode');
    return this.axiosInstance.post<ICodeAssistantRes>(url, { codes, track });
  }

  @Authorize(AuthLevel.authenticated)
  public findBug(
    codes: ICode[],
    problem: string,
    track: CourseTrack,
  ): Observable<ICodeAssistantRes> {
    const url = constructUrl(this.environmentUrl, 'findthebug');
    return this.axiosInstance.post<ICodeAssistantRes>(url, {
      codes,
      problem: problem || null,
      track
    });
  }

  @Authorize(AuthLevel.authenticated)
  public codeHint(
    codes: ICode[],
    problem: string,
    track: CourseTrack
  ): Observable<ICodeAssistantRes> {
    const url = constructUrl(this.environmentUrl, 'codehint');
    return this.axiosInstance.post<ICodeAssistantRes>(url, {
      codes,
      problem: problem || null,
      track
    });
  }

  @Authorize(AuthLevel.authenticated)
  public rate(id: string, rating: number): Observable<string> {
    const url = constructUrl(this.environmentUrl, 'rate');
    return this.axiosInstance.post<string>(url, { id, rating });
  }

  @Authorize(AuthLevel.authenticated)
  public attempt(context: number, track: CourseTrack): Observable<ContextAttempt> {
    const url = constructUrl(this.environmentUrl, 'attempt');
    return this.axiosInstance.put<ContextAttempt>(url, null, {
      params: { context, track },
    });
  }

  @Authorize(AuthLevel.authenticated)
  public use(context: number, track: CourseTrack): Observable<AttemptResType[]> {
    const url = constructUrl(this.environmentUrl, 'attempt/use');
    return this.axiosInstance.put<AttemptResType[]>(url, null, {
      params: { context, track },
    });
  }

  @Authorize(AuthLevel.authenticated)
  public getAttemptData(): Observable<AttemptResType[]> {
    const url = constructUrl(this.environmentUrl, 'attempt/all');
    return this.axiosInstance.get<AttemptResType[]>(url);
  }

  @Authorize(AuthLevel.authenticated)
  public getSolution(
    problem: string,
    encryptedCode: string,
    language: number,
    track: CourseTrack
  ): Observable<ICodeAssistantRes> {
    const url = constructUrl(this.environmentUrl, 'explainthesolution');
    return this.axiosInstance.post<ICodeAssistantRes>(url, {
      problem: problem || null,
      encryptedCode,
      language,
      track
    });
  }

  @Authorize(AuthLevel.authenticated)
  public alternativeLessonExplanation(
    courseName: string,
    content: string,
    alias: string,
  ): Observable<ICodeAssistantRes> {
    return this.axiosInstance.post(
      constructUrl(this.environmentUrl, 'alternativelessonexplanation'),
      { courseName, content, alias },
    );
  }

  @Authorize(AuthLevel.authenticated)
  public realLifeExamples(
    courseName: string,
    content: string,
    alias: string,
  ): Observable<ICodeAssistantRes> {
    return this.axiosInstance.post(
      constructUrl(this.environmentUrl, 'reallifeexamples'),
      { courseName, content, alias },
    );
  }

  @Authorize(AuthLevel.authenticated)
  public questionHint(question: {
    questionType: string;
    options: string[];
    content: string[];
    courseName: string;
    alias: string;
  }): Observable<ICodeAssistantRes> {
    return this.axiosInstance.post(
      constructUrl(this.environmentUrl, 'questionhint'),
      { ...question },
    );
  }

  @Authorize(AuthLevel.authenticated)
  public answerExplanation(question: {
    courseName: string;
    question: string[];
    answer: string;
    isCorrect: boolean;
    track: CourseTrack;
  }) {
    return this.fetchService.fetch(
      constructUrl(this.environmentUrl, 'answerexplanation'),
      {
        body: JSON.stringify(question),
        method: 'POST',
      },
    );
  }
}

export * from '../private/code-assistant-api/code-assistant-api.interface';
